import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { OrderFields } from 'types/order'
import { selectCurrentCompanyDetails } from 'store/billing/billing.selectors'
import { getCurrentCompanyDetailsRequest } from 'store/billing/billing.actions'
import { selectCurrentUser } from 'store/users/users.selectors'
import { useInsuranceCalculator } from './useInsuranceCalculate'

interface UseInsuranceFormProps {
  form: any
  onChange: (value: string | boolean | null, id?: string) => void
}

export const useSyncInsuredCargo = (form: any, onChange: (value: string | boolean | null, id?: string) => void) => {
  useEffect(() => {
    const cargo = String(form[OrderFields.CARGO] || '')
    const insuredCargo = String(form[OrderFields.META]?.insuredCargo || '')
    if (cargo !== insuredCargo) onChange(cargo, 'meta.insuredCargo')
  }, [form, onChange])
}

export const useInsuranceForm = ({ form, onChange }: UseInsuranceFormProps) => {
  const dispatch = useDispatch()
  const billingCompanyDetails = useSelector(selectCurrentCompanyDetails)
  const currentUser = useSelector(selectCurrentUser)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    dispatch(getCurrentCompanyDetailsRequest())
  }, [dispatch])

  useEffect(() => {
    if (initialized || !billingCompanyDetails?.inn) return

    const meta = form[OrderFields.META] || {}

    if (!meta.insuredInn && billingCompanyDetails.inn) onChange(String(billingCompanyDetails.inn), 'meta.insuredInn')
    if (!meta.insuredName && billingCompanyDetails.name) onChange(String(billingCompanyDetails.name), 'meta.insuredName')

    const loginIsEmail = currentUser?.login?.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
    if (!meta.insuredEmail && loginIsEmail) onChange(String(currentUser!.login), 'meta.insuredEmail')

    setInitialized(true)
  }, [billingCompanyDetails, currentUser, form, initialized, onChange])

  useSyncInsuredCargo(form, onChange)

  const cargo = form[OrderFields.CARGO] || ''
  const rawCost = (form[OrderFields.META]?.costOfCargo as string) || ''
  const costOfCargo = parseFloat(rawCost.replace(/\s/g, '')) || 0

  const requiredFieldsFilled = useMemo(() => {
    const meta = form[OrderFields.META] || {}
    return [
      meta.insuredInn,
      meta.insuredName,
      form[OrderFields.LOGISTIC_INN],
      form[OrderFields.ROUTE_DATE_START],
      meta.routeDateEnd,
      form[OrderFields.ROUTE_TRIP],
      form[OrderFields.CARGO],
      meta.costOfCargo,
    ].every((value) => String(value).trim())
  }, [form])

  const { tariff, insuranceCost } = useInsuranceCalculator({
    cargoName: cargo,
    costOfCargo,
    hasFranchise: true,
  })

  return {
    costOfCargo,
    requiredFieldsFilled,
    tariff,
    insuranceCost,
  }
}
